import { inject, Inject, Injectable, InjectionToken, Optional, Type } from "@angular/core";
import { PerformanceManager } from "@calaosoft/osapp-common/performance/PerformanceManager";
import { FlagService } from "../../../services/flag.service";
import { ShowMessageParamsPopup } from "../../../services/interfaces/ShowMessageParamsPopup";
import { PlatformService } from "../../../services/platform.service";
import { UiMessageService } from "../../../services/uiMessage.service";
import { EFlag } from "../../flags/models/EFlag";
import { PatchBase } from "../patches/patch-base";

export const PATCHES_CONFIG = new InjectionToken<Type<PatchBase>[]>("configForPatches");

@Injectable()
export class PatchService {
	//#region FIELDS

	private static readonly C_LOG_ID = "PATCH.S::";

	/** Tableau des patchs connus. */
	private readonly maPatches: PatchBase[];

	//#endregion

	//#region METHODS

	constructor(
		private readonly isvcMessage: UiMessageService,
		private readonly isvcFlag: FlagService,
		private readonly isvcPlatform: PlatformService,
		@Inject(PATCHES_CONFIG) @Optional() paPatchTypes: Type<PatchBase>[]
	) {
		this.maPatches = [];
		paPatchTypes?.forEach((poType: Type<PatchBase>) => {
			try {
				this.maPatches.push(inject(poType));
			} catch {
				this.onApplyPatchErrorAsync("Injection from the services injector is undefined", poType.name);
			}
		});
	}

	/** Applique tous les patchs connus du service, déterminés dans le module de l'app avec `PatchModule.forRoot([tableau des patchs])`. */
	public async applyPatchesAsync(): Promise<void> {
		await this.isvcPlatform.readyAsync;

		for (let lnIndex = 0; lnIndex < this.maPatches.length; ++lnIndex) {
			await this.applyPatchAsync(this.maPatches[lnIndex]);
		}

		this.isvcFlag.setFlagValue(EFlag.patchesDone, true);
	}

	private async applyPatchAsync(poPatch: PatchBase): Promise<void> {
		const loPerfManager = new PerformanceManager().markStart();
		const lbOkay: boolean = await poPatch.applyPatchAsync();

		if (lbOkay)
			console.info(
				`${PatchService.C_LOG_ID}Patch '${poPatch.patchDescription}' executed in ${loPerfManager.markEnd().measure()}ms.`
			);
		else {
			await this.onApplyPatchErrorAsync(
				new Error(`Patch '${poPatch.patchDescription}' application failed !`),
				poPatch.patchDescription
			);
		}
	}

	/** Log une erreur et affiche une popup pour signaler qu'une erreur est survenue lors de l'application d'un patch.
	 * @param poError Erreur survenue.
	 * @param poPatch Patch qui n'a pas pu être appliqué.
	 */
	private onApplyPatchErrorAsync(poError: any, psPatchDescription: string): Promise<void> {
		console.error(`${PatchService.C_LOG_ID}Error when applying patch '${psPatchDescription}'`, poError);

		// On attend que l'utilisateur clique sur "continuer" pour continuer l'application des patchs.
		return this.isvcMessage
			.showPopupMessageAsync<boolean>(
				new ShowMessageParamsPopup({
					message: `Erreur lors de l'application du patch :\n${psPatchDescription}`,
					header: "Erreur",
					buttons: [{ text: "Continuer", handler: () => UiMessageService.getTruthyResponse() }],
					backdropDismiss: false
				})
			) // Typage "(_): false" nécessaire pour que le compilo comprenne que ce n'est pas "boolean" mais bien "false".
			.then((_): void => {})
			.catch((_): void => {});
	}

	//#endregion
}
